// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventPalette.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventPalette.js
//
// Derives a whole page palette from one seed colour.
//
// ── Why this replaces the blurred-image background ─────────────────────────
//
// Today an invitation paints a blurred copy of its own cover behind the
// content. Three problems with that:
//
//   1. It competes with the cover. The same picture appears twice, once sharp
//      and once smeared, and the eye cannot settle.
//   2. It is expensive. A second full-size decode plus a blur pass - and on
//      Android a real backdrop blur has caused hangs here, which is why the
//      fallback paints a flat fill anyway.
//   3. It is unpredictable. A busy photo produces a busy background, so text
//      contrast varies per event and cannot be guaranteed.
//
// Apple Invites and Luma both solve it the same way, and neither blurs the
// image. Apple's own documentation: "As soon as you select a background, the
// app uses the primary color in the image to create a theme for your event."
// Luma shifts the page background to the dominant hue of the chosen artwork.
//
// So: extract ONE colour, derive a calm palette from it, and paint that. The
// cover stays the only picture on screen, contrast is guaranteed by
// construction, and the Android blur problem disappears because there is no
// backdrop blur left to perform.
//
// Pure, no imports, shared with the website so both render identically.

// ── Base83, for reading a blurhash ─────────────────────────────────────────
const B83 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz#$%*+,-.:;=?@[]^_{|}~';

function decodeBase83(str) {
  let value = 0;
  for (const ch of str) {
    const i = B83.indexOf(ch);
    if (i === -1) return null;
    value = value * 83 + i;
  }
  return value;
}

/**
 * The average colour of a BlurHash, as hex.
 *
 * A blurhash's first component (its "DC" term, characters 2-5) IS the average
 * colour of the image. So the dominant colour costs nothing: no image decode,
 * no extra column, no native module, and it works identically on web.
 *
 * @param {string} hash
 * @returns {string|null} '#rrggbb'
 */
export function averageColorFromBlurhash(hash) {
  if (typeof hash !== 'string' || hash.length < 6) return null;
  const dc = decodeBase83(hash.slice(2, 6));
  if (dc === null) return null;
  const r = (dc >> 16) & 255;
  const g = (dc >> 8) & 255;
  const b = dc & 255;
  return rgbToHex(r, g, b);
}

/**
 * Colours sampled from horizontal bands of a BlurHash, top to bottom.
 *
 * ── Why bands rather than one average ──────────────────────────────────────
 *
 * averageColorFromBlurhash above reads only the DC term, which is the average
 * of the WHOLE image. A photo of a red brick stoop with people in green shirts
 * averages to a muddy brown, and a page tinted with that brown looks nothing
 * like the photo.
 *
 * Apple Invites does not do that, and it is not blurring the cover either. Its
 * ground is a gradient sampled from different regions of the image: red at the
 * top where the brick is, green at the bottom where the shirts are. That is why
 * theirs reads as an extension of the photo and a single flat colour does not.
 *
 * The information is already in the hash. A BlurHash stores AC components as
 * well as the DC term — enough to reconstruct a low-resolution image — and
 * evaluating its basis functions at a few points costs a few dozen
 * multiplications. No image decode, no native module, identical on web.
 *
 * @param {string} hash
 * @param {number} [stops]  how many bands, top to bottom
 * @returns {string[]|null} hex colours, or null if the hash cannot be read
 */
export function gradientFromBlurhash(hash, stops = 3) {
  const decoded = decodeBlurhashComponents(hash);
  if (!decoded) return null;

  const out = [];
  for (let s = 0; s < stops; s += 1) {
    // Sample down the vertical centre. Horizontal variation is what a blur
    // would smear away anyway; the vertical progression is the part the eye
    // reads as "the photo continues below".
    const y = stops === 1 ? 0.5 : s / (stops - 1);
    out.push(samplePixel(decoded, 0.5, y));
  }
  return out;
}

/** numX/numY and the component list of a BlurHash, in linear RGB. */
function decodeBlurhashComponents(hash) {
  if (typeof hash !== 'string' || hash.length < 6) return null;

  const sizeFlag = decodeBase83(hash[0]);
  if (sizeFlag === null) return null;
  const numX = (sizeFlag % 9) + 1;
  const numY = Math.floor(sizeFlag / 9) + 1;
  if (hash.length !== 4 + 2 * numX * numY) return null;

  const quantMax = decodeBase83(hash[1]);
  if (quantMax === null) return null;
  const maxAc = (quantMax + 1) / 166;

  const dc = decodeBase83(hash.slice(2, 6));
  if (dc === null) return null;

  const colours = [[
    srgbToLinear((dc >> 16) & 255),
    srgbToLinear((dc >> 8) & 255),
    srgbToLinear(dc & 255),
  ]];

  for (let i = 1; i < numX * numY; i += 1) {
    const value = decodeBase83(hash.slice(4 + i * 2, 6 + i * 2));
    if (value === null) return null;
    colours.push([
      signedPow((Math.floor(value / (19 * 19)) - 9) / 9) * maxAc,
      signedPow(((Math.floor(value / 19) % 19) - 9) / 9) * maxAc,
      signedPow(((value % 19) - 9) / 9) * maxAc,
    ]);
  }
  return { numX, numY, colours };
}

/** The colour at (x, y), both in 0..1, as hex. */
function samplePixel({ numX, numY, colours }, x, y) {
  let r = 0;
  let g = 0;
  let b = 0;
  for (let j = 0; j < numY; j += 1) {
    for (let i = 0; i < numX; i += 1) {
      const basis = Math.cos(Math.PI * x * i) * Math.cos(Math.PI * y * j);
      const c = colours[i + j * numX];
      r += c[0] * basis;
      g += c[1] * basis;
      b += c[2] * basis;
    }
  }
  return rgbToHex(linearToSrgb(r), linearToSrgb(g), linearToSrgb(b));
}

/** BlurHash stores AC terms squared-and-signed, to spend bits where they show. */
function signedPow(v) {
  return v < 0 ? -(v * v) : v * v;
}

function srgbToLinear(value) {
  const v = value / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearToSrgb(value) {
  const v = Math.max(0, Math.min(1, value));
  const out = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(out * 255);
}

// ── Colour maths ───────────────────────────────────────────────────────────

export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function rgbToHex(r, g, b) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Relative luminance, per WCAG 2.x. */
export function luminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(rgb.r) + 0.7152 * ch(rgb.g) + 0.0722 * ch(rgb.b);
}

/** WCAG contrast ratio between two colours: 1 (identical) to 21 (black/white). */
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
}

export function rgbToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

export function hslToHex({ h, s, l }) {
  if (s === 0) {
    const v = l * 255;
    return rgbToHex(v, v, v);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return rgbToHex(hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255);
}

/** Minimum contrast for body text. WCAG AA. */
export const MIN_TEXT_CONTRAST = 4.5;

/**
 * A full page palette derived from one seed.
 *
 * The background is deliberately DARK and DESATURATED rather than the seed
 * itself. A saturated full-screen wash fights the cover and makes text hard to
 * read; a deep, slightly-tinted ground reads as "the invitation's colour"
 * without ever becoming the subject. That is the quality being copied from
 * Luma - the page looks tinted, not painted.
 *
 * @param {string} seedHex
 * @param {{scheme?: 'dark'|'light'}} [opts]
 */
export function derivePalette(seedHex, opts = {}) {
  const scheme = opts.scheme ?? 'dark';
  const hsl = rgbToHsl(seedHex);

  // No usable seed: a neutral near-black, which is the app's default ground.
  if (!hsl) return neutralPalette(scheme);

  // A near-grey seed has no hue worth carrying; tinting by it produces a muddy
  // wash, so fall back to neutral rather than amplifying noise.
  if (hsl.s < 0.08) return neutralPalette(scheme);

  const h = hsl.h;
  // Cap saturation: a vivid ground is exhausting behind a whole page.
  const s = Math.min(hsl.s, 0.45);

  if (scheme === 'light') {
    const background = hslToHex({ h, s: Math.min(s, 0.18), l: 0.97 });
    const surface = hslToHex({ h, s: Math.min(s, 0.14), l: 0.93 });
    return finish({
      scheme,
      seed: seedHex,
      background,
      surface,
      surfaceElevated: hslToHex({ h, s: Math.min(s, 0.1), l: 0.99 }),
      accent: hslToHex({ h, s: Math.max(s, 0.5), l: 0.42 }),
      border: hslToHex({ h, s: Math.min(s, 0.12), l: 0.85 }),
    });
  }

  // Dark: three closely-spaced grounds give depth WITHOUT card outlines, which
  // is how a single continuous surface still reads as layered.
  const background = hslToHex({ h, s: s * 0.55, l: 0.07 });
  const surface = hslToHex({ h, s: s * 0.5, l: 0.12 });
  return finish({
    scheme,
    seed: seedHex,
    background,
    surface,
    surfaceElevated: hslToHex({ h, s: s * 0.45, l: 0.17 }),
    accent: hslToHex({ h, s: Math.max(s, 0.55), l: 0.65 }),
    border: hslToHex({ h, s: s * 0.4, l: 0.22 }),
  });
}

function neutralPalette(scheme) {
  return scheme === 'light'
    ? finish({
        scheme, seed: null,
        background: '#FFFFFF', surface: '#F5F5F8', surfaceElevated: '#FFFFFF',
        accent: '#7C4DFF', border: '#E2E2EA',
      })
    : finish({
        scheme, seed: null,
        background: '#0B0B0F', surface: '#121218', surfaceElevated: '#1A1A22',
        accent: '#9A6BFF', border: '#24242E',
      });
}

/**
 * Picks text colours that are guaranteed readable on the derived background,
 * rather than assuming white-on-dark holds for every hue.
 */
function finish(p) {
  const onBg = pickTextColor(p.background);
  return {
    ...p,
    text: onBg.primary,
    textSecondary: onBg.secondary,
    textMuted: onBg.muted,
    /** Scrim for text sitting directly on the cover image. */
    coverScrim: 'rgba(0,0,0,0.55)',
    /** Contrast actually achieved, so it can be asserted in tests. */
    textContrast: contrastRatio(onBg.primary, p.background),
  };
}

/**
 * White or near-black body text, whichever contrasts better, plus softened
 * variants that still clear the AA threshold.
 */
export function pickTextColor(background) {
  const white = contrastRatio('#FFFFFF', background);
  const black = contrastRatio('#0B0B0F', background);

  if (white >= black) {
    return { primary: '#FFFFFF', secondary: 'rgba(255,255,255,0.78)', muted: 'rgba(255,255,255,0.58)' };
  }
  return { primary: '#0B0B0F', secondary: 'rgba(11,11,15,0.74)', muted: 'rgba(11,11,15,0.54)' };
}

/**
 * The palette for an event, from whatever colour information it has.
 *
 * Order of preference:
 *   1. custom_theme_color   - the host chose it explicitly
 *   2. theme_color          - the colour of the theme they picked
 *   3. cover_blurhash       - free average colour of the actual cover
 *   4. neutral
 *
 * The third entry used to read `theme_default_color`, which is not a column on
 * `events` and never has been — I invented the name. It therefore contributed
 * nothing, and since custom_theme_color is only set when a host explicitly
 * picks a colour, and cover_blurhash is null for every cover uploaded before
 * that column existed, the seed came out null for most real events. A null seed
 * derives the neutral palette, whose background is #0B0B0F — which is why every
 * "derived colour" surface rendered as flat black and the hue appeared not to
 * work at all. The real column is `theme_color`.
 *
 * theme_color is checked BEFORE the blurhash: a host who picked a theme chose
 * that colour, whereas the blurhash average is inferred. An explicit choice
 * should win over an inference.
 */
export function paletteForEvent(event, opts = {}) {
  const seed =
    firstHex(event?.custom_theme_color) ??
    firstHex(event?.theme_color) ??
    averageColorFromBlurhash(event?.cover_blurhash) ??
    null;

  const palette = derivePalette(seed, opts);

  // A ground sampled from the cover itself, top to bottom, for surfaces that
  // can paint a gradient. Null when there is no blurhash, in which case
  // consumers fall back to the flat `background` as before — so this is additive
  // and nothing has to handle it.
  //
  // Darkened toward the page background rather than used raw: these are the
  // cover's own colours at full brightness, and text has to sit on them.
  const sampled = gradientFromBlurhash(event?.cover_blurhash, 3);
  palette.gradient = sampled ? sampled.map((hex) => mixToward(hex, palette.background, 0.62)) : null;
  /** The undarkened samples, for a surface that sits behind its own scrim. */
  palette.gradientRaw = sampled;

  return palette;
}

/**
 * A page-ready gradient for one blurhash, darkened toward a background.
 *
 * paletteForEvent computes this for the event's own cover. This is the same
 * thing for an arbitrary hash, which is what a multi-cover carousel needs: each
 * media item carries its own blurhash, so each can contribute its own ground,
 * and the page can crossfade between them as the covers are swiped.
 *
 * @param {string} hash
 * @param {string} backgroundHex  what to darken toward
 * @param {number} [stops]
 * @returns {string[]|null}
 */
export function gradientForBlurhash(hash, backgroundHex, stops = 3) {
  const sampled = gradientFromBlurhash(hash, stops);
  if (!sampled) return null;
  return sampled.map((hex) => mixToward(hex, backgroundHex, 0.62));
}

/** Blends `hex` toward `target` by `amount` (0..1). */
function mixToward(hex, target, amount) {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  if (!a || !b) return hex;
  const t = Math.max(0, Math.min(1, amount));
  return rgbToHex(
    Math.round(a.r + (b.r - a.r) * t),
    Math.round(a.g + (b.g - a.g) * t),
    Math.round(a.b + (b.b - a.b) * t),
  );
}

function firstHex(v) {
  return typeof v === 'string' && hexToRgb(v) ? v : null;
}

/**
 * The text colour for an invitation's own surface.
 *
 * ── Auto by default, override when asked ───────────────────────────────────
 *
 * White text on a pale theme is unreadable, and that is a measurable fact
 * rather than a matter of taste — pickTextColor above compares the actual
 * contrast ratio of white and near-black against the ground and returns the
 * winner. So the app can simply get this right without asking.
 *
 * A host can still override it. Auto answers "readable?", which is not the same
 * question as "what do I want this invitation to look like" — a host putting
 * gold text on a dark maroon wedding theme is making a deliberate choice, and
 * an app that silently corrects it to white is wrong in a more annoying way
 * than one that was never smart.
 *
 * ── Why not a warning dialog ───────────────────────────────────────────────
 *
 * The third option — leave it white and warn when the theme is light — is the
 * worst of the three. It interrupts at the moment of choosing a theme, asks the
 * host to judge a contrast problem they can already see on screen, and fires
 * precisely when the right answer is obvious. Anything the app can measure, the
 * app should just do.
 *
 * ── Video themes ───────────────────────────────────────────────────────────
 *
 * A video's luminance changes frame to frame, so there is nothing stable to
 * measure. Those fall back to the theme's stored colour when it has one, and to
 * white otherwise — which is why the detail screen also lays a scrim under its
 * text on video grounds rather than relying on colour alone.
 *
 * @param {object} event
 * @returns {{primary: string, secondary: string, muted: string, isAuto: boolean}}
 */
export function invitationTextColor(event) {
  const override = typeof event?.text_color === 'string' && hexToRgb(event.text_color)
    ? event.text_color
    : null;

  if (override) {
    const rgb = hexToRgb(override);
    const a = (alpha) => `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
    return { primary: override, secondary: a(0.78), muted: a(0.58), isAuto: false };
  }

  // What the text actually sits on, in order of what wins visually.
  const ground =
    firstHex(event?.custom_theme_color)
    ?? firstHex(event?.theme_color)
    ?? averageColorFromBlurhash(event?.cover_blurhash)
    ?? null;

  // No ground to measure means the page background, which is dark.
  if (!ground) return { ...pickTextColor('#0B0B0F'), isAuto: true };

  return { ...pickTextColor(ground), isAuto: true };
}
